'use client'

/**
 * Template `chat-funnel`: simulador WhatsApp Typebot-style.
 *
 * Sequencia declarativa de steps em capture_pages.form_config.steps[]:
 *  - msg: mensagem da "atendente" (delay configuravel)
 *  - input: pergunta + campo texto/tel/email
 *  - choice: pergunta + opcoes (botoes)
 *  - branch: pula pra step X conforme resposta
 *  - calendly: botao link externo
 *  - redirect: redireciona URL
 *
 * Persistencia: localStorage durante navegacao + submit final em
 * `/api/capture/[slug]/submit` quando tem email/phone.
 *
 * Captura UTMs + referrer no mount.
 * Dispara fbq('track', 'Lead') no submit final.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import Image from 'next/image'
import type { CapturePage } from '@/lib/capturas/types'

type ChoiceOption = string | {
  label: string
  variant?: 'primary-whatsapp' | 'default'
  icon?: 'whatsapp'
  url?: string
}

function optionLabel(opt: ChoiceOption): string {
  return typeof opt === 'string' ? opt : opt.label
}
function optionVariant(opt: ChoiceOption): 'primary-whatsapp' | 'default' {
  return typeof opt === 'string' ? 'default' : opt.variant || 'default'
}
function optionIcon(opt: ChoiceOption): 'whatsapp' | null {
  return typeof opt === 'string' ? null : opt.icon || null
}
function optionUrl(opt: ChoiceOption): string | null {
  return typeof opt === 'string' ? null : opt.url || null
}

interface BusinessHoursConfig {
  tz?: string
  weekdays?: { open: string; close: string }
  weekend?: { open: string; close: string }
  online_label_business?: string
  online_label_off?: string
}

function nowInTz(tz: string): { day: number; hour: number; minute: number } | null {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      weekday: 'short', hour: '2-digit', minute: '2-digit'
    })
    const parts = fmt.formatToParts(new Date())
    const get = (t: string) => parts.find(p => p.type === t)?.value || ''
    const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
    const day = dayMap[get('weekday')]
    const hour = parseInt(get('hour'), 10)
    const minute = parseInt(get('minute'), 10)
    if (day === undefined || isNaN(hour) || isNaN(minute)) return null
    return { day, hour, minute }
  } catch {
    return null
  }
}

function nextOpenPhrase(bh?: BusinessHoursConfig): string | null {
  if (!bh) return null
  const tz = bh.tz || 'America/Sao_Paulo'
  const now = nowInTz(tz)
  if (!now) return null
  const curMin = now.hour * 60 + now.minute
  for (let offset = 0; offset < 8; offset++) {
    const checkDay = (now.day + offset) % 7
    const isWeekend = checkDay === 0 || checkDay === 6
    const hrs = isWeekend ? bh.weekend : bh.weekdays
    if (!hrs) continue
    const [oh, om] = hrs.open.split(':').map(Number)
    const openMin = oh * 60 + om
    if (offset === 0 && curMin >= openMin) continue
    if (offset === 0) return formatPhrase('hoje', oh)
    if (offset === 1) return formatPhrase('amanha', oh)
    return formatPhrase(`em ${offset} dias`, oh)
  }
  return null
}

function formatPhrase(when: string, hour: number): string {
  const hourStr = `${hour}h`
  if (when === 'hoje') return `ainda hoje às ${hourStr}`
  if (when === 'amanha' && hour <= 7) return `amanhã cedinho às ${hourStr}`
  if (when === 'amanha') return `amanhã às ${hourStr}`
  return `${when} às ${hourStr}`
}

function substituteDynamic(text: string, bh?: BusinessHoursConfig): string {
  if (!text || !text.includes('${next_open}')) return text
  const phrase = nextOpenPhrase(bh) || 'no próximo horário'
  return text.replace(/\$\{next_open\}/g, phrase)
}

function isWithinBusinessHours(bh?: BusinessHoursConfig): boolean {
  if (!bh) return true
  const tz = bh.tz || 'America/Sao_Paulo'
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      weekday: 'short', hour: '2-digit', minute: '2-digit'
    })
    const parts = fmt.formatToParts(new Date())
    const get = (t: string) => parts.find(p => p.type === t)?.value || ''
    const weekday = get('weekday')
    const hour = parseInt(get('hour'), 10)
    const minute = parseInt(get('minute'), 10)
    const isWeekend = weekday === 'Sat' || weekday === 'Sun'
    const hrs = isWeekend ? bh.weekend : bh.weekdays
    if (!hrs) return true
    const [oh, om] = hrs.open.split(':').map(Number)
    const [ch, cm] = hrs.close.split(':').map(Number)
    const cur = hour * 60 + minute
    return cur >= (oh * 60 + om) && cur < (ch * 60 + cm)
  } catch {
    return true
  }
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
    </svg>
  )
}

interface ChatStep {
  off_hours_text?: string
  kind: 'msg' | 'input' | 'choice' | 'branch' | 'calendly' | 'redirect'
  text?: string
  delay?: number
  key?: string
  label?: string
  type?: 'text' | 'tel-br' | 'email' | 'tel'
  placeholder?: string
  required?: boolean
  options?: ChoiceOption[]
  when?: { key: string; equals: string }
  goto?: number
  url?: string
}

interface ChatFormConfig {
  business_hours?: BusinessHoursConfig
  steps: ChatStep[]
  header?: {
    avatar_url?: string
    name?: string
    online_label?: string
    subtitle?: string
    cta?: { label?: string; url?: string }
  }
  default_delay_ms?: number
}

interface Props {
  capture: CapturePage
  userEmail?: string | null
  userName?: string | null
  userPhone?: string | null
}

interface ChatMessage {
  id: string
  role: 'agent' | 'user'
  text: string
  time: string
}

const LS_KEY = (slug: string) => `chat-funnel:${slug}:state`

function nowHHMM(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function ChatFunnel({ capture, userEmail, userName, userPhone }: Props) {
  const config = capture.form_config as unknown as ChatFormConfig
  const steps = config?.steps ?? []
  const header = config?.header ?? {}
  const defaultDelay = config?.default_delay_ms ?? 2000

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [stepIdx, setStepIdx] = useState(0)
  const [isOpen, setIsOpen] = useState(true)
  const isOpenRef = useRef(true)
  useEffect(() => { isOpenRef.current = isOpen }, [isOpen])
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [waitingFor, setWaitingFor] = useState<'msg' | 'typing' | 'input' | 'choice' | 'calendly' | 'done'>('typing')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [utmData, setUtmData] = useState<Record<string, string>>({})
  const scrollRef = useRef<HTMLDivElement>(null)

  // Captura UTMs + referrer no mount + restaura state do localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const utm: Record<string, string> = {}
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'gclid']) {
      const v = params.get(k)
      if (v) utm[k] = v
    }
    if (document.referrer) utm.referrer = document.referrer
    setUtmData(utm)

    // Detect business hours (client-side, TZ-aware)
    setIsOpen(isWithinBusinessHours(config?.business_hours))

    // Sem restore: F5 sempre reinicia o funil (comportamento Typebot original).
    // Antes salvava em localStorage, mas isso atrapalhava ajuste e demo.

    // Prefill se user logado
    if (userEmail || userName || userPhone) {
      setAnswers((prev) => ({
        ...prev,
        ...(userEmail ? { email: userEmail } : {}),
        ...(userName ? { name: userName } : {}),
        ...(userPhone ? { phone: userPhone } : {}),
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // localStorage removido: F5 sempre reinicia.

  // Scroll automatico (apos render do footer)
  useEffect(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
      })
    }
  }, [messages, waitingFor])

  // Resolver step atual
  const resolveStep = useCallback((idx: number): { step: ChatStep | null; idx: number } => {
    let i = idx
    while (i < steps.length) {
      const s = steps[i]
      if (s.kind === 'branch' && s.when) {
        if (answers[s.when.key] === s.when.equals && typeof s.goto === 'number') {
          i = s.goto
          continue
        }
        i += 1
        continue
      }
      // redirect agora é tratado no engine (useEffect), não aqui — evita
      // side-effect em render-time. resolveStep apenas retorna o step.
      return { step: s, idx: i }
    }
    return { step: null, idx: i }
  }, [steps, answers])

  // Motor: processa o step atual
  useEffect(() => {
    const { step, idx: resolvedIdx } = resolveStep(stepIdx)
    if (!step) {
      setWaitingFor('done')
      return
    }

    // Sync stepIdx com resolved idx quando resolveStep pulou branches/redirects.
    // Sem isso, advance pos input/choice acabava re-entrando no mesmo step.
    if (resolvedIdx !== stepIdx) {
      setStepIdx(resolvedIdx)
      return
    }

    // Redirect: navega o browser (side effect contido no useEffect, não no render)
    if (step.kind === 'redirect' && step.url) {
      setWaitingFor('done')
      if (typeof window !== 'undefined') {
        window.location.href = step.url
      }
      return
    }

    if (step.kind === 'msg' && step.text) {
      // Mostra "..." typing durante o delay, depois materializa a bolha
      setWaitingFor('typing')
      const delay = step.delay ?? defaultDelay
      const bubbleText = (!isOpenRef.current && step.off_hours_text) ? substituteDynamic(step.off_hours_text, config?.business_hours) : (step.text as string)
      const t = setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { id: `${stepIdx}-${Date.now()}`, role: 'agent', text: bubbleText, time: nowHHMM() },
        ])
        setStepIdx((idx) => idx + 1)
      }, delay)
      return () => clearTimeout(t)
    }

    if (step.kind === 'input') {
      // Mostra "..." typing durante delay, depois materializa pergunta + abre input
      const label = step.label || ''
      setWaitingFor('typing')
      const delay = step.delay ?? Math.min(defaultDelay, 1500)
      const t = setTimeout(() => {
        if (label) {
          setMessages((prev) => [...prev, { id: `q-${stepIdx}`, role: 'agent', text: label, time: nowHHMM() }])
        }
        setWaitingFor('input')
      }, delay)
      return () => clearTimeout(t)
    }

    if (step.kind === 'choice') {
      const label = step.label || ''
      setWaitingFor('typing')
      const delay = step.delay ?? Math.min(defaultDelay, 1500)
      const t = setTimeout(() => {
        if (label) {
          setMessages((prev) => [...prev, { id: `c-${stepIdx}`, role: 'agent', text: label, time: nowHHMM() }])
        }
        setWaitingFor('choice')
      }, delay)
      return () => clearTimeout(t)
    }

    if (step.kind === 'calendly') {
      setWaitingFor('calendly')
      return
    }
  }, [stepIdx, resolveStep, defaultDelay])

  // Submit ao backend (capture API)
  const submitFinal = useCallback(async (finalAnswers: Record<string, string>) => {
    if (submitted || submitting) return
    const hasIdentity = !!(finalAnswers.email || finalAnswers.phone)
    if (!hasIdentity) return // Submit so quando tem email ou phone

    setSubmitting(true)
    setError(null)
    try {
      const payload = { ...finalAnswers, ...utmData }
      const res = await fetch(`/api/capture/${capture.slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (data.ok) {
        setSubmitted(true)
        // Limpa state local pos-submit
        // nada a limpar (sem localStorage)
        // Fire Meta Pixel Lead event
        if (typeof window !== 'undefined' && typeof (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq === 'function') {
          ;(window as unknown as { fbq: (...args: unknown[]) => void }).fbq('track', 'Lead', { content_name: capture.slug })
        }
      } else {
        setError(data.error || 'Falha ao enviar')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro de rede')
    } finally {
      setSubmitting(false)
    }
  }, [capture.slug, utmData, submitted, submitting])

  // Quando answers tem phone OU email, dispara submit (suficiente pra lead)
  useEffect(() => {
    if (submitted) return
    if (answers.phone || answers.email) {
      void submitFinal(answers)
    }
  }, [answers, submitted, submitFinal])

  // Handler de input
  const onSubmitInput = (key: string, value: string) => {
    if (!value.trim()) return
    const newAnswers = { ...answers, [key]: value.trim() }
    setAnswers(newAnswers)
    setMessages((prev) => [...prev, { id: `u-${stepIdx}`, role: 'user', text: value.trim(), time: nowHHMM() }])
    setStepIdx((idx) => idx + 1)
    setWaitingFor('msg')
  }

  // Handler de choice
  const onChoice = (key: string, option: string) => {
    const newAnswers = { ...answers, [key]: option }
    setAnswers(newAnswers)
    setMessages((prev) => [...prev, { id: `u-${stepIdx}`, role: 'user', text: option, time: nowHHMM() }])
    setStepIdx((idx) => idx + 1)
    setWaitingFor('msg')
  }

  const { step: currentStep } = resolveStep(stepIdx)

  return (
    <div className="h-dvh sm:h-full bg-[#0a0f14] text-white flex flex-col overflow-x-hidden overscroll-none">
      {/* Header WhatsApp-like */}
      <header className="bg-[#202c33] border-b border-[#0a1014] px-3 sm:px-4 py-3 sm:pt-12 flex items-center gap-3 sticky top-0 z-10 w-full">
        {header.avatar_url ? (
          <Image
            src={header.avatar_url}
            alt={header.name || capture.title}
            width={40}
            height={40}
            className="w-10 h-10 rounded-full object-cover"
            unoptimized
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center font-semibold">
            {(header.name || capture.title || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[15px] truncate">{header.name || capture.title}</div>
          <div className={`text-[12px] flex items-center gap-1 ${isOpen ? 'text-emerald-300' : 'text-amber-300'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-emerald-400' : 'bg-amber-400'}`} />
            {isOpen
              ? (config?.business_hours?.online_label_business || header.online_label || 'online')
              : substituteDynamic(config?.business_hours?.online_label_off || 'fora do horário', config?.business_hours)}
          </div>
        </div>
        {header.cta?.url ? (
          <a href={header.cta.url} className="shrink-0 ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white text-[12px] font-semibold px-3 py-2 transition whitespace-nowrap">
            {header.cta.label || 'Agendar'}
          </a>
        ) : null}
      </header>

      {/* Mensagens */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 py-4 space-y-2 bg-[#0a0f14] [overflow-anchor:none]">
        {messages.map((m) => (
          <ChatBubble key={m.id} role={m.role} text={m.text} time={m.time} />
        ))}
        {waitingFor === 'typing' && <TypingIndicator />}
        {submitted && (
          <div className="text-center text-emerald-400 text-[13px] py-3">
            ✓ Recebemos suas informacoes!
          </div>
        )}
        {error && (
          <div className="text-center text-red-400 text-[13px] py-2">
            {error}
          </div>
        )}
      </div>

      {/* Footer: input ou choice ou calendly */}
      {waitingFor === 'input' && currentStep?.kind === 'input' && (
        <ChatInputForm step={currentStep} onSubmit={onSubmitInput} />
      )}
      {waitingFor === 'choice' && currentStep?.kind === 'choice' && (
        <ChatChoiceForm step={currentStep} onSelect={onChoice} />
      )}
      {waitingFor === 'calendly' && currentStep?.kind === 'calendly' && currentStep.url && (
        <div data-chat-footer className="shrink-0 bg-[#202c33] border-t border-[#0a1014] px-3 sm:px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] w-full">
          <a
            href={currentStep.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-center py-3 rounded-lg transition"
          >
            Agendar agora
          </a>
        </div>
      )}
    </div>
  )
}

function ChatBubble({ role, text, time }: { role: 'agent' | 'user'; text: string; time?: string }) {
  const isAgent = role === 'agent'
  return (
    <div className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`max-w-[85%] sm:max-w-[80%] px-3 py-2 rounded-lg text-[14px] leading-relaxed whitespace-pre-wrap break-words relative ${
          isAgent ? 'bg-[#202c33] text-white' : 'bg-[#005c4b] text-white'
        }`}
      >
        <span>{text}</span>
        {time && (
          <span className="inline-block ml-2 text-[10px] text-stone-400 align-bottom" style={{ float: 'right', marginLeft: '8px', marginTop: '4px' }}>
            {time}
          </span>
        )}
      </div>
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-[#202c33] px-4 py-3 rounded-lg flex gap-1">
        <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  )
}

function ChatInputForm({ step, onSubmit }: { step: ChatStep; onSubmit: (key: string, value: string) => void }) {
  const [value, setValue] = useState('')

  const [error, setError] = useState<string | null>(null)

  function validate(): boolean {
    const v = value.trim()
    if (step.type === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) {
        setError('Email invalido. Use formato: nome@dominio.com')
        return false
      }
    }
    if (step.type === 'tel-br' || step.type === 'tel') {
      const digits = v.replace(/\D/g, '')
      if (digits.length < 10 || digits.length > 11) {
        setError('Numero invalido. Use DDD + numero (10 ou 11 digitos)')
        return false
      }
      // Primeiro digito apos DDD (celular) deve ser 9 em SP+ ou 6/7/8/9 em outros estados
      const firstDigit = digits[2]
      if (digits.length === 11 && !['6', '7', '8', '9'].includes(firstDigit)) {
        setError('Numero invalido. Celular comeca com 6, 7, 8 ou 9 apos o DDD')
        return false
      }
    }
    setError(null)
    return true
  }

  function handle(e: React.FormEvent) {
    e.preventDefault()
    if (!value.trim()) return
    if (!step.key) return
    if (!validate()) return
    onSubmit(step.key, value)
    setValue('')
    setError(null)
  }

  const inputType = step.type === 'email' ? 'email' : step.type === 'tel-br' || step.type === 'tel' ? 'tel' : 'text'

  return (
    <form onSubmit={handle} data-chat-footer className="shrink-0 bg-[#202c33] border-t border-[#0a1014] px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] w-full">
      {error && (
        <div className="text-red-400 text-[12px] pb-2 px-1">{error}</div>
      )}
      <div className="flex gap-2 items-center">
      <input
        type={inputType}
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={step.placeholder || 'Digite sua resposta...'}
        required={step.required}
        style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff', caretColor: '#ffffff', fontSize: '16px' }}
        className="flex-1 min-w-0 bg-[#2a3942] border border-[#3b4a54] rounded-full px-4 py-3 text-white placeholder-stone-400 focus:outline-none focus:border-emerald-500"
      />
      <button
        type="submit"
        className="shrink-0 px-5 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 transition disabled:opacity-40 font-semibold text-white text-[15px]"
        disabled={!value.trim()}
      >
        Enviar
      </button>
      </div>
    </form>
  )
}

function ChatChoiceForm({ step, onSelect }: { step: ChatStep; onSelect: (key: string, option: string) => void }) {
  if (!step.options || !step.key) return null
  return (
    <div data-chat-footer className="shrink-0 bg-[#202c33] border-t border-[#0a1014] px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex flex-col gap-2 w-full max-h-[60vh] overflow-y-auto">
      {step.options.map((opt) => {
        const label = optionLabel(opt)
        const variant = optionVariant(opt)
        const icon = optionIcon(opt)
        const isPrimary = variant === 'primary-whatsapp'
        const cls = isPrimary
          ? 'w-full bg-[#25D366] hover:bg-[#1ebe5b] text-white rounded-lg px-4 py-3 text-[15px] font-semibold transition flex items-center justify-center gap-2 shadow-md'
          : 'w-full bg-[#2a3942] hover:bg-[#3b4a54] border border-[#3b4a54] rounded-lg px-4 py-3 text-[14px] text-white transition text-left'
        const url = optionUrl(opt)
        const inner = (
          <>
            {icon === 'whatsapp' && <WhatsAppIcon className="w-5 h-5 shrink-0" />}
            <span>{label}</span>
          </>
        )
        // Link nativo (<a>) quando a opcao tem url: deep-link mais robusto no
        // navegador in-app do Facebook/Instagram (evita navegacao via JS diferida).
        if (url) {
          return (
            <a key={label} href={url} onClick={() => step.key && onSelect(step.key, label)} className={cls}>
              {inner}
            </a>
          )
        }
        return (
          <button key={label} onClick={() => step.key && onSelect(step.key, label)} className={cls}>
            {inner}
          </button>
        )
      })}
    </div>
  )
}
